import React, { useEffect, useState } from 'react';
import { getTenant } from '../../../theme/tokens';
import { BRAND } from '../../../shared/config/brand';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, CalendarIcon, PercentIcon, InfoIcon, TagIcon, TagsIcon, TicketIcon, BanknoteIcon, RefreshCwIcon, CreditCardIcon, GlobeIcon, HashIcon, LayersIcon, SearchIcon, PlusIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { Campaign } from '../../../shared/types/Campaign';
import { FormField } from '../../../shared/ui/FormField';
import { SearchableSelect } from '../../../shared/ui/SearchableSelect';
// Mock campaigns data - would be replaced with API calls in a real app
const mockCampaigns: Record<string, any> = {
  BILASHOP2025: {
    id: 'BILASHOP2025',
    title: '[BILA Mart] 7-days Promotion for Shop',
    description: '[BILA Mart] 7-days Promotion for Shop',
    startDate: '01/03/2026',
    expiryDate: '07/03/2026',
    discountPercentage: 0,
    fixedDiscountAmount: 50,
    currency: getTenant().currency.code,
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budgetType: 'fixed',
    budget: 5000,
    strictlyEnforceBudget: true,
    paymentMethods: ['Card', 'Mobile Banking'],
    cardTargetingMode: 'all',
    cardBrands: [],
    cardBin: '',
    cardIssuingCountry: '',
    cardType: 'All Types',
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'BILASHOP2025',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 50,
    maxDiscount: 50,
    status: 'active'
  },
  BILA10MYR: {
    id: 'BILA10MYR',
    title: `BILA Mart Special Package (${getTenant().currency.code} 10 Discount)`,
    description: 'DISCOUNT_COUPON',
    startDate: '01/01/2026',
    expiryDate: '02/01/2026',
    discountPercentage: 0,
    fixedDiscountAmount: 10,
    currency: getTenant().currency.code,
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budgetType: 'fixed',
    budget: 11000,
    strictlyEnforceBudget: true,
    paymentMethods: ['Card'],
    cardTargetingMode: 'bins',
    cardBrands: ['Visa', 'Mastercard'],
    cardBin: '411111',
    cardIssuingCountry: '',
    cardType: 'All Types',
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'BILA10MYR',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 10,
    maxDiscount: 10,
    status: 'active'
  },
  BULK2025: {
    id: 'BULK2025',
    title: 'Summer Festival Bulk Codes',
    description: 'Unique codes for summer festival attendees',
    startDate: '01/11/2025',
    expiryDate: '31/01/2026',
    discountPercentage: 15,
    fixedDiscountAmount: 0,
    currency: getTenant().currency.code,
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budgetType: 'fixed',
    budget: 50000,
    strictlyEnforceBudget: true,
    paymentMethods: ['Card', 'PromptPay'],
    cardTargetingMode: 'all',
    cardBrands: [],
    cardBin: '',
    cardIssuingCountry: '',
    cardType: 'All Types',
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: '',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Bulk',
    minTransactionValue: 500,
    maxTransactionValue: 999999,
    minDiscount: 0,
    maxDiscount: 1000,
    status: 'active'
  }
};
// Empty campaign template for new campaigns
const emptyCampaign: any = {
  id: '',
  title: '',
  description: '',
  startDate: '',
  expiryDate: '',
  discountPercentage: 0,
  fixedDiscountAmount: 0,
  currency: getTenant().currency.code,
  discountCarriedBy: 'Merchant',
  merchantPercentage: 0,
  budgetType: 'fixed',
  budget: 0,
  recurringBudget: 0,
  recurringBudgetPeriod: 'monthly',
  strictlyEnforceBudget: true,
  paymentMethods: [],
  cardTargetingMode: 'all',
  cardBrands: [],
  cardBin: '',
  cardIssuingCountry: '',
  cardIssuingCountries: [],
  cardType: 'All Types',
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: '',
  category: 'Discount code',
  owner: 'Merchant',
  codeStrategy: 'Global',
  minTransactionValue: 0,
  maxTransactionValue: 999999,
  minDiscount: 0,
  maxDiscount: 0,
  status: 'active'
};
const RECURRING_PERIODS = ['Daily', 'Weekly', 'Monthly'];
const CARD_TYPES = ['Credit', 'Debit', 'Prepaid'];
const CARD_CATEGORIES = ['Consumer', 'Commercial', 'Corporate', 'Premium', 'Platinum', 'Gold', 'Classic'];
const CARD_RULE_DEFINITIONS = [{
  id: 'brands',
  label: 'Card Brands',
  icon: CreditCardIcon,
  description: 'Filter by Visa, Mastercard, etc.'
}, {
  id: 'types',
  label: 'Card Type',
  icon: LayersIcon,
  description: 'Filter by Credit, Debit, Prepaid'
}, {
  id: 'bins',
  label: 'Card BIN',
  icon: HashIcon,
  description: 'Filter by Bank Identification Numbers'
}, {
  id: 'countries',
  label: 'Issuing Country',
  icon: GlobeIcon,
  description: 'Filter by card issuing country'
}, {
  id: 'categories',
  label: 'Card Category',
  icon: CreditCardIcon,
  description: 'Filter by Consumer, Commercial, etc.'
}] as const;
const PAYMENT_METHODS_LIST = ['Card', 'Mobile Banking', 'LINE Pay', BRAND.cardName, 'WeChat Pay', 'Alipay', 'TrueMoney Wallet', 'PromptPay', 'QR Payment', 'Installment', 'Internet Banking'];
const CARD_BRANDS = ['Visa', 'Mastercard', 'UnionPay', 'American Express', 'JCB', 'TPN', 'Discover', 'Diners Club'];
const COUNTRIES = [{
  code: 'TH',
  name: 'Thailand'
}, {
  code: 'US',
  name: 'United States'
}, {
  code: 'GB',
  name: 'United Kingdom'
}, {
  code: 'SG',
  name: 'Singapore'
}, {
  code: 'JP',
  name: 'Japan'
}, {
  code: 'CN',
  name: 'China'
}, {
  code: 'KR',
  name: 'South Korea'
}, {
  code: 'MY',
  name: 'Malaysia'
}, {
  code: 'ID',
  name: 'Indonesia'
}, {
  code: 'PH',
  name: 'Philippines'
}, {
  code: 'VN',
  name: 'Vietnam'
}, {
  code: 'IN',
  name: 'India'
}, {
  code: 'AU',
  name: 'Australia'
}, {
  code: 'NZ',
  name: 'New Zealand'
}, {
  code: 'HK',
  name: 'Hong Kong'
}, {
  code: 'TW',
  name: 'Taiwan'
}, {
  code: 'DE',
  name: 'Germany'
}, {
  code: 'FR',
  name: 'France'
}, {
  code: 'IT',
  name: 'Italy'
}, {
  code: 'ES',
  name: 'Spain'
}, {
  code: 'NL',
  name: 'Netherlands'
}, {
  code: 'CH',
  name: 'Switzerland'
}, {
  code: 'SE',
  name: 'Sweden'
}, {
  code: 'CA',
  name: 'Canada'
}, {
  code: 'BR',
  name: 'Brazil'
}, {
  code: 'MX',
  name: 'Mexico'
}, {
  code: 'AE',
  name: 'United Arab Emirates'
}, {
  code: 'SA',
  name: 'Saudi Arabia'
}, {
  code: 'RU',
  name: 'Russia'
}, {
  code: 'ZA',
  name: 'South Africa'
}, {
  code: 'KH',
  name: 'Cambodia'
}, {
  code: 'LA',
  name: 'Laos'
}, {
  code: 'MM',
  name: 'Myanmar'
}, {
  code: 'BD',
  name: 'Bangladesh'
}, {
  code: 'LK',
  name: 'Sri Lanka'
}];
interface ChoiceCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  selected: boolean;
  onClick: () => void;
}
function ChoiceCard({
  title,
  description,
  icon,
  selected,
  onClick
}: ChoiceCardProps) {
  return <div onClick={onClick} className={`cursor-pointer rounded-lg border p-4 transition-all duration-200 flex items-start gap-3 ${selected ? 'border-[#E2231A] bg-[#FEE7E5] shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-11881" data-mp="211u2j94nol2l">
      <div className="flex-shrink-0 mt-0.5" data-id="element-11882" data-mp="f3gr3ybl1d13">
        <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${selected ? 'border-[#E2231A] bg-[#E2231A]' : 'border-gray-300 bg-white'}`} data-id="element-11883" data-mp="f25o40pyo9b1">
          {selected && <div className="w-1.5 h-1.5 rounded-full bg-white" data-id="element-11884" data-mp="573yl3rrx57x" />}
        </div>
      </div>
      <div className="flex-1" data-id="element-11885" data-mp="5obl661comw">
        <div className="flex items-center gap-2 mb-1" data-id="element-11886" data-mp="8kbv15cvpei">
          <span className={`flex-shrink-0 ${selected ? 'text-[#9F140F]' : 'text-gray-500'}`} data-id="element-11887" data-mp="3sba0jlnkgh58">
            {icon}
          </span>
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-11888" data-mp="3ndibczeb394q">{title}</h3>
        </div>
        <p className="text-sm text-gray-500" data-id="element-11889" data-mp="1qgswweqwk5pw">{description}</p>
      </div>
    </div>;
}
interface CountryMultiSelectProps {
  selectedCountries: Record<string, boolean>;
  onToggle: (code: string) => void;
  countrySearch: string;
  onSearchChange: (val: string) => void;
  filteredCountries: {
    code: string;
    name: string;
  }[];
  selectedCountryList: {
    code: string;
    name: string;
  }[];
}
function CountryMultiSelect({
  selectedCountries,
  onToggle,
  countrySearch,
  onSearchChange,
  filteredCountries,
  selectedCountryList
}: CountryMultiSelectProps) {
  const {
    t
  } = useTranslation('shops');
  const [isOpen, setIsOpen] = useState(false);
  return <div className="relative" data-id="element-11890" data-mp="1f4bs7me7bhkp">
      {/* Trigger / Display */}
      <div onClick={() => setIsOpen(!isOpen)} className="w-full min-h-[42px] px-3 py-2 border border-gray-200 rounded-lg text-sm cursor-pointer flex flex-wrap items-center gap-1.5 bg-white hover:border-gray-300 transition-colors" data-id="element-11891" data-mp="1k2vejnwpivdl">
        {selectedCountryList.length > 0 ? <>
            {selectedCountryList.map(c => <span key={c.code} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#FEE7E5] border border-[#E2231A]/30 text-[#C41B14] text-xs font-medium" data-id="element-11892" data-mp="2zlgqcnq3gmqs">
                {c.code}
                <button type="button" onClick={e => {
            e.stopPropagation();
            onToggle(c.code);
          }} className="text-[#C41B14]/60 hover:text-red-700 ml-0.5 leading-none" data-id="element-11893" data-mp="1gnyjhtfitmcl">
                  ×
                </button>
              </span>)}
          </> : <span className="text-gray-400" data-id="element-11894" data-mp="xxh04fgkw33w">
            {t('pages.CountryMultiSelect.select_countries')}
          </span>}
      </div>

      {/* Dropdown */}
      {isOpen && <>
          <div className="fixed inset-0 z-10" onClick={() => {
        setIsOpen(false);
        onSearchChange('');
      }} data-id="element-11895" data-mp="29dp0ca8jf2ce" />

          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-11896" data-mp="2owxrqwyreb9">
            <div className="p-2 border-b border-gray-100" data-id="element-11897" data-mp="3tuy2lhqc5bog">
              <div className="relative" data-id="element-11898" data-mp="3txkc3mwse19b">
                <SearchIcon size={14} className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-11899" data-mp="3p3ptkmsx3sbt" />

                <input type="text" value={countrySearch} onChange={e => onSearchChange(e.target.value)} placeholder={t('pages.CountryMultiSelect.attrs.search_countries')} className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A] focus:outline-none" autoFocus onClick={e => e.stopPropagation()} data-id="element-11900" data-mp="3f61yfvregit4" />
              </div>
            </div>
            <div className="max-h-52 overflow-y-auto" data-id="element-11901" data-mp="2tve61vozkix0">
              {filteredCountries.length > 0 ? filteredCountries.map(country => <div key={country.code} onClick={e => {
            e.stopPropagation();
            onToggle(country.code);
          }} className={`flex items-center justify-between px-3 py-2 cursor-pointer text-sm transition-colors ${selectedCountries[country.code] ? 'bg-[#FEE7E5] text-[#C41B14]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-11902" data-mp="2ynnu166jj8wn">
                    <span data-id="element-11903" data-mp="32o7nnzrwnue1">
                      <span className="font-medium" data-id="element-11904" data-mp="32mwjvzygmmmx">{country.code}</span>
                      <span className="ml-2 text-gray-500" data-id="element-11905" data-mp="37m9ghcf4v82p">{country.name}</span>
                    </span>
                    {selectedCountries[country.code] && <span className="text-[#9F140F] text-xs font-bold" data-id="element-11906" data-mp="1ravr89yh6e1w">
                        ✓
                      </span>}
                  </div>) : <div className="px-3 py-4 text-sm text-gray-400 text-center" data-id="element-11907" data-mp="9awglt63vhqi">
                  {t('pages.CountryMultiSelect.no_countries_found')}
                </div>}
            </div>
          </div>
        </>}
    </div>;
}
export function CampaignForm() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [campaign, setCampaign] = useState<any>(emptyCampaign);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [discountType, setDiscountType] = useState<'fixed' | 'percentage'>('fixed');
  const [budgetType, setBudgetType] = useState<'fixed' | 'recurring'>('fixed');
  const [activeCardRules, setActiveCardRules] = useState<string[]>([]);
  const [showAddRuleMenu, setShowAddRuleMenu] = useState(false);
  const [selectedCardTypes, setSelectedCardTypes] = useState<Record<string, boolean>>({});
  const [selectedCardCategories, setSelectedCardCategories] = useState<Record<string, boolean>>({});
  const [selectedDays, setSelectedDays] = useState<Record<string, boolean>>({
    Sunday: true,
    Monday: true,
    Tuesday: true,
    Wednesday: true,
    Thursday: true,
    Friday: true,
    Saturday: true
  });
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<Record<string, boolean>>({});
  const [selectedCardBrands, setSelectedCardBrands] = useState<Record<string, boolean>>(CARD_BRANDS.reduce((acc, brand) => {
    acc[brand] = true;
    return acc;
  }, {} as Record<string, boolean>));
  const [selectedCountries, setSelectedCountries] = useState<Record<string, boolean>>({});
  const [countrySearch, setCountrySearch] = useState('');
  useEffect(() => {
    const campaignId = searchParams.get('id');
    if (campaignId && mockCampaigns[campaignId]) {
      const loadedCampaign = mockCampaigns[campaignId];
      setCampaign(loadedCampaign);
      setIsEditing(true);
      setDiscountType(loadedCampaign.discountPercentage > 0 ? 'percentage' : 'fixed');
      setBudgetType(loadedCampaign.budgetType || 'fixed');
      // Load active card rules from campaign data
      const rules: string[] = [];
      if (loadedCampaign.cardBrands?.length > 0) rules.push('brands');
      if (loadedCampaign.cardType && loadedCampaign.cardType !== 'All Types') rules.push('types');
      if (loadedCampaign.cardBin) rules.push('bins');
      if (loadedCampaign.cardIssuingCountries?.length > 0) rules.push('countries');
      if (loadedCampaign.cardCategories?.length > 0) rules.push('categories');
      setActiveCardRules(rules);
      // Set selected days based on campaign data
      const days = loadedCampaign.validDays.reduce((acc: Record<string, boolean>, day: string) => {
        acc[day] = true;
        return acc;
      }, {});
      setSelectedDays(days);
      // Set selected payment methods
      const methods = (loadedCampaign.paymentMethods || []).reduce((acc: Record<string, boolean>, method: string) => {
        acc[method] = true;
        return acc;
      }, {});
      setSelectedPaymentMethods(methods);
      // Set selected card brands
      const brands = (loadedCampaign.cardBrands || []).reduce((acc: Record<string, boolean>, brand: string) => {
        acc[brand] = true;
        return acc;
      });
      setSelectedCardBrands(brands);
      // Set selected countries
      const countries = (loadedCampaign.cardIssuingCountries || []).reduce((acc: Record<string, boolean>, code: string) => {
        acc[code] = true;
        return acc;
      });
      setSelectedCountries(countries);
    }
    setLoading(false);
  }, [searchParams]);
  const navigateBack = () => {
    if (isEditing) {
      navigate(`/campaign-details?id=${campaign.id}`);
    } else {
      navigate('/campaigns');
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setCampaign({
        ...campaign,
        [name]: checkbox.checked
      });
      return;
    }
    if (type === 'number') {
      setCampaign({
        ...campaign,
        [name]: parseFloat(value) || 0
      });
      return;
    }
    setCampaign({
      ...campaign,
      [name]: value
    });
  };
  const handleDayToggle = (day: string) => {
    const newSelectedDays = {
      ...selectedDays,
      [day]: !selectedDays[day]
    };
    setSelectedDays(newSelectedDays);
    const validDays = Object.entries(newSelectedDays).filter(([_, isSelected]) => isSelected).map(([day]) => day);
    setCampaign({
      ...campaign,
      validDays
    });
  };
  const handlePaymentMethodToggle = (method: string) => {
    const newSelectedMethods = {
      ...selectedPaymentMethods,
      [method]: !selectedPaymentMethods[method]
    };
    setSelectedPaymentMethods(newSelectedMethods);
    const paymentMethods = Object.entries(newSelectedMethods).filter(([_, isSelected]) => isSelected).map(([method]) => method);
    setCampaign({
      ...campaign,
      paymentMethods
    });
  };
  const handleCardBrandToggle = (brand: string) => {
    const newSelectedBrands = {
      ...selectedCardBrands,
      [brand]: !selectedCardBrands[brand]
    };
    setSelectedCardBrands(newSelectedBrands);
    const cardBrands = Object.entries(newSelectedBrands).filter(([_, isSelected]) => isSelected).map(([brand]) => brand);
    setCampaign({
      ...campaign,
      cardBrands
    });
  };
  const handleCountryToggle = (code: string) => {
    const newSelectedCountries = {
      ...selectedCountries,
      [code]: !selectedCountries[code]
    };
    setSelectedCountries(newSelectedCountries);
    const cardIssuingCountries = Object.entries(newSelectedCountries).filter(([_, isSelected]) => isSelected).map(([code]) => code);
    setCampaign({
      ...campaign,
      cardIssuingCountries
    });
  };
  const handleCardTypeToggle = (type: string) => {
    const newSelected = {
      ...selectedCardTypes,
      [type]: !selectedCardTypes[type]
    };
    setSelectedCardTypes(newSelected);
    const cardTypes = Object.entries(newSelected).filter(([_, v]) => v).map(([k]) => k);
    setCampaign({
      ...campaign,
      cardTypes
    });
  };
  const handleCardCategoryToggle = (cat: string) => {
    const newSelected = {
      ...selectedCardCategories,
      [cat]: !selectedCardCategories[cat]
    };
    setSelectedCardCategories(newSelected);
    const cardCategories = Object.entries(newSelected).filter(([_, v]) => v).map(([k]) => k);
    setCampaign({
      ...campaign,
      cardCategories
    });
  };
  const addCardRule = (ruleId: string) => {
    if (!activeCardRules.includes(ruleId)) {
      setActiveCardRules([...activeCardRules, ruleId]);
    }
    setShowAddRuleMenu(false);
  };
  const removeCardRule = (ruleId: string) => {
    setActiveCardRules(activeCardRules.filter(r => r !== ruleId));
    // Clear the rule's data
    if (ruleId === 'brands') {
      setSelectedCardBrands({});
      setCampaign({
        ...campaign,
        cardBrands: []
      });
    } else if (ruleId === 'types') {
      setSelectedCardTypes({});
      setCampaign({
        ...campaign,
        cardTypes: []
      });
    } else if (ruleId === 'bins') {
      setCampaign({
        ...campaign,
        cardBin: ''
      });
    } else if (ruleId === 'countries') {
      setSelectedCountries({});
      setCampaign({
        ...campaign,
        cardIssuingCountries: []
      });
    } else if (ruleId === 'categories') {
      setSelectedCardCategories({});
      setCampaign({
        ...campaign,
        cardCategories: []
      });
    }
  };
  const availableRules = CARD_RULE_DEFINITIONS.filter(r => !activeCardRules.includes(r.id));
  const filteredCountries = COUNTRIES.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()) || c.code.toLowerCase().includes(countrySearch.toLowerCase()));
  const selectedCountryList = COUNTRIES.filter(c => selectedCountries[c.code]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Submitting campaign:', {
      ...campaign,
      activeCardRules
    });
    navigate('/campaigns');
  };
  if (loading) {
    return <div className="flex justify-center items-center h-64" data-id="element-11908" data-mp="2yc5lfjc1etz5">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E2231A]" data-id="element-11909" data-mp="334yqy9fby6ca"></div>
      </div>;
  }
  const inputClasses = 'w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] focus:outline-none transition-colors';
  const amountInputClasses = 'w-full pl-12 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] focus:outline-none transition-colors';
  return <div className="max-w-4xl mx-auto space-y-6 pb-12" data-id="element-11910" data-mp="3mrlnlc8sfo1d">
      {/* Header */}
      <div data-id="element-11911" data-mp="3iq08rlcp0zoj">
        <button onClick={navigateBack} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold mb-4" data-id="element-11912" data-mp="3ipi5w5vizn7n">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-11913" data-mp="2q1ckdgwndu6r" />
          {t('pages.CampaignDetails.back_to_campaigns')}
        </button>
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-11914" data-mp="341x4qhi7ny2d">
          {isEditing ? 'Update Campaign' : 'Create Campaign'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-11915" data-mp="2z3vn810fo6t0">
        {/* Section 1: Campaign Details */}
        <div className="p-6 border-b border-gray-100" data-id="element-11916" data-mp="ebaau0rsnyce">
          <h2 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wider" data-id="element-11917" data-mp="msaxp3duj7yl">
            {t('pages.CampaignDetails.campaign_details')}
          </h2>
          <div className="space-y-6" data-id="element-11918" data-mp="qrt8h6bye2np">
            <FormField label={t('pages.CampaignForm.attrs.title')} required data-id="element-11919" data-mp="cv6ar1c6f8gq">
              <input type="text" name="title" value={campaign.title} onChange={handleInputChange} required placeholder={t('pages.CampaignForm.attrs.eg_summer_sale_2025')} className={inputClasses} data-id="element-11920" data-mp="ht7or7uqwhlc" />
            </FormField>

            <FormField label={t('pages.CampaignDetails.description')} data-id="element-11921" data-mp="vspjs4ymwr35">
              <textarea name="description" value={campaign.description} onChange={handleInputChange} rows={3} placeholder={t('pages.CampaignForm.attrs.brief_description_of_the_campaign')} className={`${inputClasses} resize-none`} data-id="element-11922" data-mp="10rk4jcg5wppw" />
            </FormField>
          </div>
        </div>

        {/* Section 2: Code Strategy */}
        <div className="p-6 border-b border-gray-100" data-id="element-11923" data-mp="3rr9l9acl0ol1">
          <h2 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wider" data-id="element-11924" data-mp="3rs1eieipia85">
            {t('pages.CampaignDetails.campaign_code')}
          </h2>
          <div className="space-y-6" data-id="element-11925" data-mp="ob2gf0ifklao">
            <FormField label={t('components.CampaignList.code_strategy')} required data-id="element-11926" data-mp="o9zqovwugowc">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-11927" data-mp="je2dgygvnyns">
                <ChoiceCard title={t('pages.CampaignForm.attrs.single_global_code')} description={t('pages.CampaignForm.attrs.one_code_that_can_be_used')} icon={<TagIcon size={18} data-id="element-11929" data-mp="12au27nf6v5z8" />} selected={campaign.codeStrategy === 'Global'} onClick={() => setCampaign({
                ...campaign,
                codeStrategy: 'Global'
              })} data-id="element-11928" data-mp="ej54tsnifrbf" />

                <ChoiceCard title={t('pages.CampaignForm.attrs.unique_bulk_codes')} description={t('pages.CampaignForm.attrs.system_generates_multiple_unique_codes_for')} icon={<TagsIcon size={18} data-id="element-11931" data-mp="1sii2m3y0iip3" />} selected={campaign.codeStrategy === 'Bulk'} onClick={() => setCampaign({
                ...campaign,
                codeStrategy: 'Bulk'
              })} data-id="element-11930" data-mp="1jjd3avemmlbv" />
              </div>
            </FormField>

            {campaign.codeStrategy === 'Global' && <FormField label={t('pages.CampaignForm.attrs.global_campaign_code')} required data-id="element-11932" data-mp="2c6h56rifajjf">
                <input type="text" name="code" value={campaign.code} onChange={handleInputChange} required placeholder={t('pages.CampaignForm.attrs.eg_summer25')} className={inputClasses} data-id="element-11933" data-mp="h4m1ohhfxiwe" />
              </FormField>}
          </div>
        </div>

        {/* Section 3: Discount Configuration */}
        <div className="p-6 border-b border-gray-100" data-id="element-11934" data-mp="zxzq03qner31">
          <h2 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wider" data-id="element-11935" data-mp="uxlhylp1rh50">
            {t('pages.CampaignForm.discount_configuration')}
          </h2>
          <div className="space-y-6" data-id="element-11936" data-mp="14uzsy5s7bdpx">
            <FormField label={t('pages.CampaignForm.attrs.discount_type')} required data-id="element-11937" data-mp="14w14sch1k48p">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-11938" data-mp="3ao7ydx49zuyp">
                <ChoiceCard title={t('pages.CampaignForm.attrs.fixed_amount')} description={t('pages.CampaignForm.attrs.a_specific_usd_amount_off_the')} icon={<BanknoteIcon size={18} data-id="element-11940" data-mp="30m3wl5tfo42o" />} selected={discountType === 'fixed'} onClick={() => {
                setDiscountType('fixed');
                setCampaign({
                  ...campaign,
                  discountPercentage: 0
                });
              }} data-id="element-11939" data-mp="36ne84x3d242n" />

                <ChoiceCard title={t('pages.CampaignForm.attrs.percentage')} description={t('pages.CampaignForm.attrs.a_percentage_off_the_total_purchase')} icon={<PercentIcon size={18} data-id="element-11942" data-mp="28uxvon5w22cc" />} selected={discountType === 'percentage'} onClick={() => {
                setDiscountType('percentage');
                setCampaign({
                  ...campaign,
                  fixedDiscountAmount: 0
                });
              }} data-id="element-11941" data-mp="2hukwb6qsn44c" />
              </div>
            </FormField>

            {discountType === 'fixed' ? <FormField label={t('pages.CampaignForm.attrs.discount_amount')} required data-id="element-11943" data-mp="15vb33eurnbgp">
                <div className="relative" data-id="element-11944" data-mp="11u932vt0yvdo">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-11945" data-mp="wvxs2hqzuetv">
                    <span className="text-gray-500 font-medium" data-id="element-11946" data-mp="rvs7tmn1zult">
                      {campaign.currency}
                    </span>
                  </div>
                  <input type="number" name="fixedDiscountAmount" value={campaign.fixedDiscountAmount || ''} onChange={handleInputChange} required min="0" step="0.01" placeholder="0.00" className={amountInputClasses} data-id="element-11947" data-mp="mzb9jzm53zv0" />
                </div>
              </FormField> : <FormField label={t('pages.CampaignForm.attrs.discount_percentage')} required data-id="element-11948" data-mp="3zf5b4qocvt0">
                <div className="relative" data-id="element-11949" data-mp="cta7q50ew84y">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-11950" data-mp="cs0atj5ap5zw">
                    <span className="text-gray-500 font-medium" data-id="element-11951" data-mp="3c9zw6qdlltvd">%</span>
                  </div>
                  <input type="number" name="discountPercentage" value={campaign.discountPercentage || ''} onChange={handleInputChange} required min="0" max="100" step="0.1" placeholder="0" className={amountInputClasses} data-id="element-11952" data-mp="3h8b6t3bpw8en" />
                </div>
              </FormField>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-11953" data-mp="1wl3dtdd5wcs4">
              <FormField label={t('pages.CampaignForm.attrs.minimum_transaction_value')} data-id="element-11954" data-mp="21jd9rq6pnhes">
                <div className="relative" data-id="element-11955" data-mp="1mt5xjs7aqumj">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-11956" data-mp="1ms4jrfywbu14">
                    <span className="text-gray-500 font-medium" data-id="element-11957" data-mp="1dtaj9eig2jia">
                      {campaign.currency}
                    </span>
                  </div>
                  <input type="number" name="minTransactionValue" value={campaign.minTransactionValue || ''} onChange={handleInputChange} min="0" step="0.01" placeholder="0.00" className={amountInputClasses} data-id="element-11958" data-mp="1hwfk7cmvmd4t" />
                </div>
              </FormField>

              {discountType === 'percentage' && <FormField label={t('pages.CampaignForm.attrs.maximum_discount_amount')} data-id="element-11959" data-mp="rln7tckcj0dq">
                  <div className="relative" data-id="element-11960" data-mp="mnlocqj0cz1q">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-11961" data-mp="11b6t0ovj76br">
                      <span className="text-gray-500 font-medium" data-id="element-11962" data-mp="wjooxry7opmk">
                        {campaign.currency}
                      </span>
                    </div>
                    <input type="number" name="maxDiscount" value={campaign.maxDiscount || ''} onChange={handleInputChange} min="0" step="0.01" placeholder="0.00" className={amountInputClasses} data-id="element-11963" data-mp="1ad66x8b91uah" />
                  </div>
                </FormField>}
            </div>
          </div>
        </div>

        {/* Section 4: Budget & Schedule */}
        <div className="p-6 border-b border-gray-100" data-id="element-11964" data-mp="ally7xvyj27g">
          <h2 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wider" data-id="element-11965" data-mp="em5yvbfqonyc">
            {t('pages.CampaignForm.budget_schedule')}
          </h2>
          <div className="space-y-6" data-id="element-11966" data-mp="jjo00fnilyvs">
            <FormField label={t('pages.CampaignForm.attrs.budget_type')} data-id="element-11967" data-mp="okbvbdis11vt">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-11968" data-mp="oi8zudyuugjp">
                <ChoiceCard title={t('pages.CampaignForm.attrs.fixed_budget')} description={t('pages.CampaignForm.attrs.a_single_budget_for_the_entire')} icon={<BanknoteIcon size={18} data-id="element-11970" data-mp="12e2y83205nik" />} selected={budgetType === 'fixed'} onClick={() => {
                setBudgetType('fixed');
                setCampaign({
                  ...campaign,
                  budgetType: 'fixed'
                });
              }} data-id="element-11969" data-mp="tin91zc6zwjb" />

                <ChoiceCard title={t('pages.CampaignForm.attrs.recurring_budget')} description={t('pages.CampaignForm.attrs.budget_resets_daily_weekly_or_monthly')} icon={<RefreshCwIcon size={18} data-id="element-11972" data-mp="2rup0kwzon0f5" />} selected={budgetType === 'recurring'} onClick={() => {
                setBudgetType('recurring');
                setCampaign({
                  ...campaign,
                  budgetType: 'recurring'
                });
              }} data-id="element-11971" data-mp="31s33ruwlhspi" />
              </div>
            </FormField>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-11973" data-mp="35sn231swn2d8">
              {budgetType === 'fixed' ? <FormField label={t('pages.CampaignForm.attrs.total_budget')} required data-id="element-11974" data-mp="72d6dy5ujuto">
                  <div className="relative" data-id="element-11975" data-mp="2b4wz24io9gi">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-11976" data-mp="3izaw3gr0ntxu">
                      <span className="text-gray-500 font-medium" data-id="element-11977" data-mp="3ny4arci1hqzl">
                        {campaign.currency}
                      </span>
                    </div>
                    <input type="number" name="budget" value={campaign.budget || ''} onChange={handleInputChange} required min="0" step="0.01" placeholder="0.00" className={amountInputClasses} data-id="element-11978" data-mp="392y54qmuvu6s" />
                  </div>
                </FormField> : <>
                  <FormField label={t('pages.CampaignForm.attrs.recurring_budget_amount')} required data-id="element-11979" data-mp="rld7enkg66r">
                    <div className="relative" data-id="element-11980" data-mp="tbtqid1ems2j">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-11981" data-mp="2dzcm1x5aqz0z">
                        <span className="text-gray-500 font-medium" data-id="element-11982" data-mp="290i570qw3kji">
                          {campaign.currency}
                        </span>
                      </div>
                      <input type="number" name="recurringBudget" value={campaign.recurringBudget || ''} onChange={handleInputChange} required min="0" step="0.01" placeholder="0.00" className={amountInputClasses} data-id="element-11983" data-mp="2iqkxszu3mwhb" />
                    </div>
                  </FormField>
                  <FormField label={t('pages.CampaignForm.attrs.reset_period')} required data-id="element-11984" data-mp="3fglurm4mgj9p">
                    <select name="recurringBudgetPeriod" value={campaign.recurringBudgetPeriod} onChange={handleInputChange as any} className={inputClasses} data-id="element-11985" data-mp="3bhursydh67eo">
                      {RECURRING_PERIODS.map(period => <option key={period} value={period.toLowerCase()} data-id="element-11986" data-mp="2m4dmyapyjzao">
                          {period}
                        </option>)}
                    </select>
                  </FormField>
                </>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-11987" data-mp="1ub3i5a4ikyk2">
              <FormField label={t('pages.CampaignForm.attrs.start_date')} required data-id="element-11988" data-mp="1ydi0zu86nybc">
                <div className="relative" data-id="element-11989" data-mp="237maak4x8ifl">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-11990" data-mp="16ookrwhs6l1r">
                    <CalendarIcon size={16} className="text-gray-400" data-id="element-11991" data-mp="1aofarxzhgx58" />
                  </div>
                  <input type="date" name="startDate" value={campaign.startDate ? campaign.startDate.split('/').reverse().join('-') : ''} onChange={e => {
                  const date = e.target.value;
                  if (date) {
                    const [year, month, day] = date.split('-');
                    setCampaign({
                      ...campaign,
                      startDate: `${day}/${month}/${year}`
                    });
                  } else {
                    setCampaign({
                      ...campaign,
                      startDate: ''
                    });
                  }
                }} required className={amountInputClasses} data-id="element-11992" data-mp="134wkd6nwu1mm" />
                </div>
              </FormField>

              <FormField label={t('pages.CampaignForm.attrs.end_date')} data-id="element-11993" data-mp="1keqgch23py7w">
                <div className="relative" data-id="element-11994" data-mp="1pdjxcjglk7ct">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-11995" data-mp="1pb7g68iny10t">
                    <CalendarIcon size={16} className="text-gray-400" data-id="element-11996" data-mp="1bfmw39r77crt" />
                  </div>
                  <input type="date" name="expiryDate" value={campaign.expiryDate ? campaign.expiryDate.split('/').reverse().join('-') : ''} onChange={e => {
                  const date = e.target.value;
                  if (date) {
                    const [year, month, day] = date.split('-');
                    setCampaign({
                      ...campaign,
                      expiryDate: `${day}/${month}/${year}`
                    });
                  } else {
                    setCampaign({
                      ...campaign,
                      expiryDate: ''
                    });
                  }
                }} className={amountInputClasses} data-id="element-11997" data-mp="1geq3h0unhv2y" />
                </div>
              </FormField>
            </div>

            <FormField label={t('pages.CampaignForm.attrs.valid_days')} data-id="element-11998" data-mp="93lqigwx17nc">
              <div className="flex flex-wrap gap-2" data-id="element-11999" data-mp="mz7dkijl9hd4">
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => <button key={day} type="button" onClick={() => handleDayToggle(day)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedDays[day] ? 'bg-[#FEE7E5] text-[#C41B14] border border-[#E2231A]/30' : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'}`} data-id="element-12000" data-mp="rttqiq5bjhzb">
                    {day.substring(0, 3)}
                  </button>)}
              </div>
            </FormField>
          </div>
        </div>

        {/* Section 5: Targeting Rules */}
        <div className="p-6" data-id="element-12001" data-mp="1ecph81hsw4bo">
          <div className="flex items-center justify-between mb-6" data-id="element-12002" data-mp="1s55ml8y6cefr">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-12003" data-mp="1n6vq8v0pr7sk">
              {t('pages.CampaignForm.targeting_rules')}
            </h2>
            <div className="relative" data-id="element-12004" data-mp="223mhgh08hd73">
              <button type="button" onClick={() => setShowAddRuleMenu(!showAddRuleMenu)} disabled={availableRules.length === 0} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${availableRules.length === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#FEE7E5] text-[#9F140F] hover:bg-[#F1786F]'}`} data-id="element-12005" data-mp="1x51pt5z1s1u9">
                <PlusIcon size={16} data-id="element-12006" data-mp="2jcf9pfwgd5r0" />
                {t('pages.CampaignForm.attrs.add_rule')}
              </button>

              {showAddRuleMenu && availableRules.length > 0 && <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowAddRuleMenu(false)} data-id="element-12007" data-mp="29mchhhx5nvtq" />

                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-12008" data-mp="1zp7uxgzedyce">
                    {availableRules.map(rule => <button key={rule.id} type="button" onClick={() => addCardRule(rule.id)} className="w-full flex items-start px-4 py-3 hover:bg-gray-50 text-left" data-id="element-12009" data-mp="1ls1fnwdvuhrx">
                        <rule.icon size={18} className="text-gray-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-12010" data-mp="3l0q1gbosfr4h" />

                        <div data-id="element-12011" data-mp="3ekzc24g2hujn">
                          <div className="text-sm font-medium text-gray-900" data-id="element-12012" data-mp="3eowyp01ut48a">
                            {rule.label}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5" data-id="element-12013" data-mp="34pft0imlqqcb">
                            {rule.description}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </>}
            </div>
          </div>

          <div className="space-y-6" data-id="element-12014" data-mp="3skqtyg9pfv2x">
            <FormField label={t('pages.CampaignForm.attrs.payment_methods')} data-id="element-12015" data-mp="3nn8oizuwbxzo">
              <div className="flex flex-wrap gap-2" data-id="element-12016" data-mp="3nl5um4qmhkpo">
                {PAYMENT_METHODS_LIST.map(method => <button key={method} type="button" onClick={() => handlePaymentMethodToggle(method)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedPaymentMethods[method] ? 'bg-[#FEE7E5] text-[#C41B14] border border-[#E2231A]/30' : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'}`} data-id="element-12017" data-mp="k6ibi10wckoh">
                    {method}
                  </button>)}
              </div>
            </FormField>

            {activeCardRules.length === 0 ? <div className="bg-gray-50 border border-gray-200 border-dashed rounded-lg p-6 text-center" data-id="element-12018" data-mp="1ffdiwee1gsjy">
                <CreditCardIcon size={24} className="mx-auto text-gray-400 mb-2" data-id="element-12019" data-mp="1k6lp71k2nxsw" />

                <h3 className="text-sm font-medium text-gray-900 mb-1" data-id="element-12020" data-mp="1p43u8guyxsvm">
                  {t('pages.CampaignForm.no_card_targeting_rules')}
                </h3>
                <p className="text-sm text-gray-500" data-id="element-12021" data-mp="1z3crpjp1lpmq">
                  {t('pages.CampaignForm.attrs.this_campaign_will_apply_to')}
                </p>
              </div> : <div className="space-y-4" data-id="element-12022" data-mp="2qvwq7chorzd0">
                {activeCardRules.includes('brands') && <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 relative group" data-id="element-12023" data-mp="35kjdq1hgvxfb">
                    <button type="button" onClick={() => removeCardRule('brands')} className="absolute top-4 right-4 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors opacity-0 group-hover:opacity-100" data-id="element-12024" data-mp="30lycbyqhb7pa">
                      <XIcon size={16} data-id="element-12025" data-mp="2lx24o2sc3wp7" />
                    </button>
                    <FormField label={t('pages.CampaignForm.attrs.eligible_card_brands')} data-id="element-12026" data-mp="2hv89ga0wrkwf">
                      <div className="flex flex-wrap gap-2 mt-2" data-id="element-12027" data-mp="15jkqewo1yqm">
                        {CARD_BRANDS.map(brand => <button key={brand} type="button" onClick={() => handleCardBrandToggle(brand)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedCardBrands[brand] ? 'bg-[#FEE7E5] text-[#C41B14] border border-[#E2231A]/30' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`} data-id="element-12028" data-mp="3t9u3yp16y9f7">
                            {brand}
                          </button>)}
                      </div>
                    </FormField>
                  </div>}

                {activeCardRules.includes('types') && <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 relative group" data-id="element-12029" data-mp="io9y8l4s039j">
                    <button type="button" onClick={() => removeCardRule('types')} className="absolute top-4 right-4 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors opacity-0 group-hover:opacity-100" data-id="element-12030" data-mp="16duhl54ud2jc">
                      <XIcon size={16} data-id="element-12031" data-mp="1g0yiixnu59dj" />
                    </button>
                    <FormField label={t('pages.CampaignForm.attrs.eligible_card_types')} data-id="element-12032" data-mp="1fznecwqh9zlw">
                      <div className="flex flex-wrap gap-2 mt-2" data-id="element-12033" data-mp="171l55twlc0nj">
                        {CARD_TYPES.map(type => <button key={type} type="button" onClick={() => handleCardTypeToggle(type)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedCardTypes[type] ? 'bg-[#FEE7E5] text-[#C41B14] border border-[#E2231A]/30' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`} data-id="element-12034" data-mp="122gxx8t88czu">
                            {type}
                          </button>)}
                      </div>
                    </FormField>
                  </div>}

                {activeCardRules.includes('categories') && <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 relative group" data-id="element-12035" data-mp="2v0nj67uzvz57">
                    <button type="button" onClick={() => removeCardRule('categories')} className="absolute top-4 right-4 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors opacity-0 group-hover:opacity-100" data-id="element-12036" data-mp="2zvjat6wbkkhs">
                      <XIcon size={16} data-id="element-12037" data-mp="2g9wrcztel023" />
                    </button>
                    <FormField label={t('pages.CampaignForm.attrs.eligible_card_categories')} data-id="element-12038" data-mp="2g7k2s3t53rg2">
                      <div className="flex flex-wrap gap-2 mt-2" data-id="element-12039" data-mp="1oi8japhnqzp2">
                        {CARD_CATEGORIES.map(cat => <button key={cat} type="button" onClick={() => handleCardCategoryToggle(cat)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedCardCategories[cat] ? 'bg-[#FEE7E5] text-[#C41B14] border border-[#E2231A]/30' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`} data-id="element-12040" data-mp="1shpt1v44aj01">
                            {cat}
                          </button>)}
                      </div>
                    </FormField>
                  </div>}

                {activeCardRules.includes('bins') && <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 relative group" data-id="element-12041" data-mp="19m2ijbkd8art">
                    <button type="button" onClick={() => removeCardRule('bins')} className="absolute top-4 right-4 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors opacity-0 group-hover:opacity-100" data-id="element-12042" data-mp="1nlbzidgeb8wu">
                      <XIcon size={16} data-id="element-12043" data-mp="276lz8epwg5ro" />
                    </button>
                    <FormField label={t('pages.CampaignForm.attrs.target_card_bins')} data-id="element-12044" data-mp="1t8nmfdr88hea">
                      <input type="text" name="cardBin" value={campaign.cardBin || ''} onChange={handleInputChange} placeholder={t('pages.CampaignForm.attrs.enter_first_6_or_8_digits')} className={`${inputClasses} bg-white mt-2`} data-id="element-12045" data-mp="1y5w2n8stebcw" />
                    </FormField>
                  </div>}

                {activeCardRules.includes('countries') && <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 relative group" data-id="element-12046" data-mp="15hi50u5n1h1v">
                    <button type="button" onClick={() => removeCardRule('countries')} className="absolute top-4 right-4 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors opacity-0 group-hover:opacity-100" data-id="element-12047" data-mp="12vnv2y2szqbs">
                      <XIcon size={16} data-id="element-12048" data-mp="1gvoxihypv5td" />
                    </button>
                    <FormField label={t('pages.CampaignForm.attrs.eligible_issuing_countries')} data-id="element-12049" data-mp="1lsfcf1ysbvd7">
                      <div className="mt-2" data-id="element-12050" data-mp="1vj1kmb469jlv">
                        <CountryMultiSelect selectedCountries={selectedCountries} onToggle={handleCountryToggle} countrySearch={countrySearch} onSearchChange={setCountrySearch} filteredCountries={filteredCountries} selectedCountryList={selectedCountryList} data-id="element-12051" data-mp="1vk3679itdohu" />
                      </div>
                    </FormField>
                  </div>}
              </div>}
          </div>
        </div>

        {/* Form Actions */}
        <div className="p-6 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3" data-id="element-12052" data-mp="25n8b9j4fkxl1">
          <button type="button" onClick={navigateBack} className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-12053" data-mp="32d7ss32k76fr">
            {t('pages.CampaignDetails.cancel')}
          </button>
          <button type="submit" className="px-6 py-2.5 bg-[#E2231A] hover:bg-[#EB4A42] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-12054" data-mp="2omm41xqfc29y">
            {isEditing ? 'Save Changes' : 'Create Campaign'}
          </button>
        </div>
      </form>
    </div>;
}